import { useMemo, useState } from "react";
import { Alert, Anchor, Badge, Box, Button, Card, Grid, Group, NavLink, ScrollArea, Skeleton, Stack, Tabs, Text } from "@mantine/core";
import { IconDownload, IconFileCode, IconShieldLock } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { fmtNumber } from "@passio/api-client";
import { PageHeader } from "../theme/components";
import { CodeBlock } from "../components/JsonView";
import { Readme } from "../components/Readme";
import { AccountCallout } from "../components/AccountCta";
import { useSession } from "../lib/session";
import passioReadme from "../../../packages/passio-api-client/README.md?raw";
import easyaiReadme from "../../../packages/easyai-client/README.md?raw";

interface SdkPackage {
  name: string;
  version: string;
  description: string;
  file: string;
  latest: string;
  bytes: number;
  integrity: string;
}

const SOURCES = import.meta.glob(["../../../packages/*/src/**/*.ts", "!**/*.test.ts", "!**/generated/**"], { query: "?raw", import: "default" }) as Record<string, () => Promise<string>>;

const PACKAGES = [
  { dir: "passio-api-client", name: "@passio/api-client", label: "Passio API client", readme: passioReadme },
  { dir: "easyai-client", name: "@passio/easyai-client", label: "Easy AI client", readme: easyaiReadme },
];

const relPath = (key: string) => key.replace(/^.*\/packages\/[^/]+\/src\//, "");

function SourceBrowser({ dir }: { dir: string }) {
  const files = useMemo(
    () =>
      Object.keys(SOURCES)
        .filter((k) => k.includes(`/packages/${dir}/src/`))
        .sort((a, b) => relPath(a).split("/").length - relPath(b).split("/").length || relPath(a).localeCompare(relPath(b))),
    [dir],
  );
  const [file, setFile] = useState(() => files.find((f) => f.endsWith("/index.ts")) ?? files[0]);
  const src = useQuery({ queryKey: ["sdk-source", file], queryFn: () => SOURCES[file](), enabled: Boolean(file), staleTime: Infinity });
  return (
    <Grid gutter="md">
      <Grid.Col span={{ base: 12, md: 3 }}>
        <ScrollArea.Autosize mah={560}>
          {files.map((f) => (
            <NavLink key={f} label={relPath(f)} active={f === file} onClick={() => setFile(f)} leftSection={<IconFileCode size={14} />} styles={{ label: { fontFamily: "var(--mantine-font-family-monospace)", fontSize: 12 } }} style={{ borderRadius: 8 }} />
          ))}
        </ScrollArea.Autosize>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 9 }}>
        {src.isPending ? <Skeleton h={300} radius="md" /> : <CodeBlock code={src.data ?? ""} maxHeight={560} language="ts" />}
        <Text fz="xs" c="dimmed" mt={6}>
          Tests and the generated OpenAPI types (<code>src/generated/openapi.ts</code>) are left out of this view; the generated types ship in the package.
        </Text>
      </Grid.Col>
    </Grid>
  );
}

/** Install the demo's own typed clients, read their quick-starts and browse the source. */
export function DevelopersPage() {
  const origin = window.location.origin;
  const manifest = useQuery({
    queryKey: ["sdk-manifest"],
    queryFn: async () => {
      const res = await fetch("/sdk/index.json");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return ((await res.json()) as { packages: SdkPackage[] }).packages;
    },
    staleTime: Infinity,
  });
  const [tab, setTab] = useState(PACKAGES[0].dir);
  const prospect = useSession().me?.user.authKind !== "api_key";

  return (
    <>
      <PageHeader
        eyebrow="Client SDKs · TypeScript"
        title="Use these clients in your app"
        description="Every page here runs on two small typed clients: one for the Passio Nutrition API and one for Easy AI chains. They're served from this site as npm packages, built from the same source the demo runs, with no runtime dependencies."
      />
      {prospect && (
        <Box mb="lg">
          <AccountCallout placement="developers" title="Get the license key these clients need" body="Create your Passio account to get your API key and pick a plan, then install the packages below." />
        </Box>
      )}
      <Alert color="violet" variant="light" icon={<IconShieldLock size={18} />} mb="lg" title="Keys stay on your server">
        In this demo the browser never sees a key: calls go through the demo server, which adds credentials. Do the same in your app: use the clients server-side with your license key and Easy AI key, or mint short-lived tokens for the browser. The packages contain no keys.
      </Alert>
      <Tabs value={tab} onChange={(v) => v && setTab(v)} variant="pills" radius="xl">
        <Tabs.List mb="md">
          {PACKAGES.map((p) => (
            <Tabs.Tab key={p.dir} value={p.dir}>
              {p.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        {PACKAGES.map((p) => {
          const pkg = manifest.data?.find((m) => m.name === p.name);
          const url = pkg ? `${origin}/sdk/${pkg.latest}` : "";
          return (
            <Tabs.Panel key={p.dir} value={p.dir}>
              <Stack gap="lg">
                <Card>
                  <Group justify="space-between" mb="sm" wrap="wrap">
                    <Group gap="xs">
                      <Text fw={750} ff="monospace">
                        {p.name}
                      </Text>
                      {pkg && <Badge variant="light">v{pkg.version}</Badge>}
                      {pkg && (
                        <Text fz="xs" c="dimmed">
                          {fmtNumber(pkg.bytes / 1024)} KB
                        </Text>
                      )}
                    </Group>
                    {pkg && (
                      <Group gap="xs">
                        <Button component="a" href={`/sdk/${pkg.latest}`} download size="xs" variant="default" leftSection={<IconDownload size={14} />}>
                          Download
                        </Button>
                        <Anchor href={`/sdk/${pkg.file}`} fz="xs">
                          pinned v{pkg.version}
                        </Anchor>
                      </Group>
                    )}
                  </Group>
                  {manifest.isPending && <Skeleton h={60} />}
                  {manifest.error && (
                    <Text fz="sm" c="dimmed">
                      The packages are built by <code>npm run build</code> (scripts/build-sdk.mjs), so they're missing in the Vite dev server. The source and quick-start below still apply.
                    </Text>
                  )}
                  {pkg && (
                    <Stack gap="xs">
                      <CodeBlock code={`npm install ${url}`} language="shell" />
                      <Text fz="xs" c="dimmed">
                        Works with npm, pnpm, yarn and bun. To pin a version, install the pinned file instead. Using the package in a monorepo or CI? Commit the downloaded .tgz and install it from a path.
                      </Text>
                    </Stack>
                  )}
                </Card>
                <Card>
                  <Readme markdown={p.readme} skipTitle />
                </Card>
                <Card>
                  <Text fw={700} mb="sm">
                    Source
                  </Text>
                  <SourceBrowser dir={p.dir} />
                </Card>
              </Stack>
            </Tabs.Panel>
          );
        })}
      </Tabs>
    </>
  );
}
